import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getPackageLabel } from "@/lib/planFeatures";

interface PremiumRequestRow {
  id: string;
  user_id: string;
  plan_name: string;
  status: string;
}

/**
 * Subscribes to realtime UPDATEs on `premium_requests` for the current user.
 * - Approved → success toast + invalidate subscription caches.
 * - Rejected → warning toast.
 * Also persists an in-app notification (idempotent via dedupe_key).
 */
export function useRealtimePremiumRequests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const seenRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!user) return;
    seenRef.current.clear();

    const isTr = i18n.language?.startsWith("tr");

    // Persisted dedupe across sessions: once we've shown the approval/rejection
    // toast for a given request id, never show it again on subsequent logins.
    const STORAGE_KEY = `premium-request-toast-shown:${user.id}`;
    const loadShown = (): Record<string, string> => {
      try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}"); }
      catch { return {}; }
    };
    const markShown = (id: string, status: string) => {
      try {
        const m = loadShown();
        m[id] = status;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
      } catch { /* ignore */ }
    };

    const handleStatusChange = async (row: PremiumRequestRow) => {
      const prev = seenRef.current.get(row.id);
      seenRef.current.set(row.id, row.status);
      if (prev && prev === row.status) return;
      if (row.status !== "approved" && row.status !== "rejected") return;
      // Skip if we already surfaced this status for this request in a past session.
      const persisted = loadShown();
      if (persisted[row.id] === row.status) return;
      markShown(row.id, row.status);


      const planLabel = getPackageLabel(row.plan_name as any) || row.plan_name;

      if (row.status === "approved") {
        // Refresh caches so premium content unlocks immediately.
        queryClient.invalidateQueries({ queryKey: ["pending-premium-request"] });
        queryClient.invalidateQueries({ queryKey: ["premium-requests"] });
        queryClient.invalidateQueries({ queryKey: ["active-subscriptions"] });
        queryClient.invalidateQueries({ queryKey: ["subscription-status"] });
        queryClient.invalidateQueries({ queryKey: ["user-programs"] });

        toast.success(
          isTr ? "Aboneliğin onaylandı" : "Your subscription is approved",
          {
            description: isTr
              ? `${planLabel} paketine erişimin aktif. Premium içeriklere git.`
              : `${planLabel} access is active. Open premium content now.`,
            action: {
              label: isTr ? "Premium" : "Premium",
              onClick: () => navigate("/premium#current-plan"),
            },
            duration: 8000,
          },
        );

        await supabase.from("notifications").upsert(
          {
            user_id: user.id,
            type: "premium_request_approved",
            title: isTr ? "Aboneliğin onaylandı" : "Your subscription is approved",
            body: isTr
              ? `${planLabel} paketine erişimin aktif. Premium içeriklere göz at.`
              : `${planLabel} access is active. Explore premium content.`,
            link: "/premium#current-plan",
            dedupe_key: `premium_request_approved:${row.id}`,
          },
          { onConflict: "user_id,dedupe_key", ignoreDuplicates: true },
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["pending-premium-request"] });
        queryClient.invalidateQueries({ queryKey: ["premium-requests"] });

        toast.warning(
          isTr ? "Talebin onaylanmadı" : "Your request was not approved",
          {
            description: isTr
              ? `${planLabel} paketi için talebin reddedildi. Detay için talep geçmişini incele.`
              : `Your ${planLabel} request was rejected. See request history for details.`,
            action: {
              label: isTr ? "Talepleri Gör" : "View Requests",
              onClick: () => navigate("/premium/requests"),
            },
            duration: 8000,
          },
        );

        await supabase.from("notifications").upsert(
          {
            user_id: user.id,
            type: "premium_request_rejected",
            title: isTr ? "Talebin onaylanmadı" : "Your request was not approved",
            body: isTr
              ? `${planLabel} paketi için talebin reddedildi.`
              : `Your ${planLabel} request was rejected.`,
            link: "/premium/requests",
            dedupe_key: `premium_request_rejected:${row.id}`,
          },
          { onConflict: "user_id,dedupe_key", ignoreDuplicates: true },
        );
      }
    };

    const channel = supabase
      .channel(`premium-requests:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "premium_requests",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as PremiumRequestRow;
          if (row?.user_id === user.id) handleStatusChange(row);
        },
      )
      .subscribe();

    // Polling fallback (every 30s) in case the realtime channel drops.
    const pollId = window.setInterval(async () => {
      const { data } = await supabase
        .from("premium_requests")
        .select("id, user_id, plan_name, status")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false })
        .limit(5);
      (data ?? []).forEach((r) => handleStatusChange(r as PremiumRequestRow));
    }, 30_000);

    return () => {
      window.clearInterval(pollId);
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, navigate]);
}
